import React from 'react';

import { Blog } from '../../components/Blogs/blog/Blog';
import { BlogPageInterface } from '../../api/blogPage';
import axios from 'axios';
import Head from 'next/head';
import { convertTimestampToYYYYMMDD } from '../../utils/getDateTime';
import Image from 'next/image';
import router from 'next/router';
import { replaceTripshepherdWithSeeSight } from '../../utils/trip-sheppered-text';

export default function SingleBlogPage(blogData: BlogPageInterface) {
  return (
    <>
      <Head>
        <meta
          name="og:title"
          content={replaceTripshepherdWithSeeSight(blogData?.blog.meta_title)}
        />
        <meta
          name="og:description"
          content={replaceTripshepherdWithSeeSight(
            blogData?.blog.meta_description
          )}
          key="metadescription"
        />
        <title>
          {replaceTripshepherdWithSeeSight(blogData?.blog.meta_title)}
        </title>
        <meta
          name="og:image"
          content={replaceTripshepherdWithSeeSight(
            blogData?.blog.featured_image.image
          )}
        />
        <meta
          name="description"
          content={replaceTripshepherdWithSeeSight(
            blogData?.blog.meta_description
          )}
          key="metadescription"
        />
        <link
          href={replaceTripshepherdWithSeeSight(blogData?.blog.canonical)}
          rel="canonical"
          key="canonical"
        />
      </Head>
      {blogData?.blog ? (
        <Blog blog={replaceTripshepherdWithSeeSight(blogData?.blog)} />
      ) : (
        <div className="flex h-[calc(100vh-100px)] justify-center items-center gap-5 flex-col px-10 xl:mr-32">
          <Image
            src="/missing-data-illustration.svg"
            alt="missing data icon"
            width={272}
            height={181}
            className="w-[200px] h-[150px] sm:w-[272px] sm:h-[181px]"
          />
          <h2 className="text-black13 text-xs sm:text-base lg:text-lg font-semibold leading-none">
            No Blog Data Found!
          </h2>
          <div className="text-center text-gray75 text-xs sm:text-base font-normal max-w-[400px]">
            We couldn&apos;t find information about the blog. Please check back
            later or explore other content.
          </div>
          <button
            className="h-[32] bg-primary text-white text-center text-xs sm:text-sm px-5 rounded-[30px] py-2 sm:py-3 shadow"
            onClick={() => router.push('/')}
          >
            Explore Tours
          </button>
        </div>
      )}{' '}
    </>
  );
}

interface Params {
  params: {
    slug: string;
  };
}

export async function getServerSideProps({ params }: Params) {
  const { slug } = params;

  try {
    const response = await axios.get(
      `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blogs/${slug}`
    );

    const blogData = response.data;

    if (!blogData) {
      return {
        redirect: {
          statusCode: 301,
          destination: '/404'
        }
      };
    }

    // Transform the old blog structure to the new structure
    const transformedBlogData = {
      snippet: blogData.snippet,
      canonical: blogData.canonical,
      meta_description: blogData.meta_description,
      meta_title: blogData.meta_title
        ? blogData.meta_title
        : blogData.page_title,
      page_title: blogData.page_title,
      featured_image: {
        image: blogData.featured_image
          ? blogData.featured_image.image
          : blogData.image.url, // Use new featured image if available, otherwise use old structure
        alt_text: blogData.featured_image
          ? blogData.featured_image.alt_text || 'Featured image'
          : blogData.image.alternativeText || 'Featured image' // Fallback if not present
      },
      relatedBlogs: blogData?.relatedBlogs ?? [],
      relatedProducts: blogData?.relatedProducts ?? [],
      slug: blogData.slug,
      content: blogData.content,
      publication_date: blogData.created_at
        ? convertTimestampToYYYYMMDD(blogData.created_at)
        : convertTimestampToYYYYMMDD(blogData.published_date),
      author: blogData.author,
      city: blogData.city,
      category: blogData.category
    };
    return {
      props: {
        blog:
          slug !== 'see-sight-tours-becomes-tripshepherd'
            ? JSON.parse(JSON.stringify(transformedBlogData))
            : transformedBlogData &&
              replaceTripshepherdWithSeeSight(transformedBlogData)
      }
    };
  } catch (error) {
    console.error('Error fetching blog data:', error);
    return {
      notFound: true
    };
  }
}
